import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { isAcceptedImage, recentDocumentImages, requestImageAttachments, signedUrls } from '../../../lib/docgen/images';

// Image controls for the document editor: a drop / paste / click target, a
// thumbnail with its actions, the "pick from elsewhere" dialog and the crop
// dialog. All storage work goes through lib/docgen/images.js; these are UI.

const thumbStyle = (size) => ({
  width: size, height: size, objectFit: 'contain', background: '#fff',
  border: '1px solid #D8D0BE', borderRadius: 6, display: 'block',
});

/** Drop, paste (when focused) or click to choose. Calls onFiles(File[]). */
export function ImageDrop({ onFiles, disabled, compact, label, t }) {
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const take = (list) => {
    const files = [...(list || [])].filter(isAcceptedImage);
    if (files.length) onFiles(files);
  };
  if (disabled) return null;
  return (
    <div tabIndex={0} role="button"
      onClick={() => input.current?.click()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); } }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
      onPaste={(e) => { const f = [...(e.clipboardData?.items || [])].filter((i) => i.kind === 'file').map((i) => i.getAsFile()); if (f.length) { e.preventDefault(); take(f); } }}
      style={{
        border: `1.5px dashed ${over ? 'var(--ac-ink)' : 'var(--ac-line-2)'}`, borderRadius: 8, cursor: 'pointer',
        padding: compact ? '4px 8px' : '18px 12px', textAlign: 'center', fontSize: compact ? 11.5 : 12.5,
        color: 'var(--ac-muted)', background: over ? 'var(--ac-hover)' : 'transparent', outline: 'none', whiteSpace: 'nowrap',
      }}>
      {label || t('اسحب صورة هنا، أو الصق (Ctrl+V)، أو اضغط للاختيار', 'Drop an image, paste (Ctrl+V), or click to choose')}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden
        onChange={(e) => { take(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

/** One stored image with crop / remove / reorder actions. */
export function Thumb({ image, url, size = 56, locked, onCrop, onRemove, onMove, children }) {
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, alignItems: 'center', margin: 3 }}>
      {url ? <img src={url} alt="" style={thumbStyle(size)} /> : <div style={{ ...thumbStyle(size), background: 'var(--ac-sunk)' }} />}
      {!locked && (
        <div style={{ display: 'flex', gap: 2 }}>
          {onMove && <button type="button" className="ac-btn" style={{ height: 22, padding: '0 5px', fontSize: 11 }} onClick={() => onMove(-1)}>‹</button>}
          <button type="button" className="ac-btn" title="crop" style={{ height: 22, padding: '0 5px', fontSize: 11 }} onClick={onCrop}>✂</button>
          <button type="button" className="ac-btn" title="remove" style={{ height: 22, padding: '0 5px', fontSize: 11 }} onClick={onRemove}>✕</button>
          {onMove && <button type="button" className="ac-btn" style={{ height: 22, padding: '0 5px', fontSize: 11 }} onClick={() => onMove(1)}>›</button>}
        </div>
      )}
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, width = 760 }) {
  return (
    <div className="ac-modal-ov" onClick={onClose} style={{ zIndex: 1000 }}>
      <div className="ac-modal" onClick={(e) => e.stopPropagation()} style={{ width: `min(${width}px, 94vw)`, maxHeight: '88vh', overflow: 'auto' }}>
        <div className="ac-modal-title">{title}</div>
        {children}
      </div>
    </div>
  );
}

/** Pick an image from a previous document or the linked request's attachments. */
export function PickerModal({ docId, requestId, onPickDoc, onPickRequest, onClose, t }) {
  const [tab, setTab] = useState('docs');
  const [docs, setDocs] = useState(null);
  const [urls, setUrls] = useState({});
  const [reqImgs, setReqImgs] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    recentDocumentImages({ excludeDocId: docId }).then(async (rows) => {
      setDocs(rows);
      setUrls(await signedUrls(rows.map((r) => r.storage_path)));
    }).catch((e) => { setDocs([]); setErr(e.message); });
    if (requestId) requestImageAttachments(requestId).then(setReqImgs).catch((e) => { setReqImgs([]); setErr(e.message); });
  }, [docId, requestId]);

  const pick = async (fn) => { setBusy(true); try { await fn(); onClose(); } catch (e) { setErr(e.message); } setBusy(false); };
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 };

  return (
    <Modal title={t('اختيار صورة', 'Choose an image')} onClose={onClose}>
      <div className="ac-tabs" style={{ marginTop: 0 }}>
        <button className={`ac-tab${tab === 'docs' ? ' on' : ''}`} onClick={() => setTab('docs')}>{t('من مستندات سابقة', 'From previous documents')}</button>
        {requestId && <button className={`ac-tab${tab === 'req' ? ' on' : ''}`} onClick={() => setTab('req')}>{t('من مرفقات الطلب', 'From the request’s attachments')}</button>}
      </div>
      {err && <div className="ac-placeholder" style={{ color: 'var(--ac-danger)' }}>{err}</div>}
      {tab === 'docs' && (docs == null ? <div className="ac-skel" style={{ height: 120 }} /> : docs.length === 0
        ? <div className="ac-placeholder">{t('لا صور في مستندات سابقة.', 'No images in previous documents.')}</div>
        : (
          <div style={grid}>
            {docs.map((d) => (
              <button key={d.id} type="button" disabled={busy} className="ac-card" style={{ padding: 6, cursor: 'pointer', font: 'inherit' }}
                onClick={() => pick(() => onPickDoc(d))}>
                <img src={urls[d.storage_path]} alt="" style={{ width: '100%', height: 90, objectFit: 'contain', background: '#fff' }} />
                <div dir="ltr" style={{ fontSize: 10.5, color: 'var(--ac-muted)', marginTop: 4 }}>{d.doc_documents?.ref_no || 'DRAFT'}</div>
              </button>
            ))}
          </div>
        ))}
      {tab === 'req' && (reqImgs == null ? <div className="ac-skel" style={{ height: 120 }} /> : reqImgs.length === 0
        ? <div className="ac-placeholder">{t('لا صور مرفقة بهذا الطلب.', 'This request has no image attachments.')}</div>
        : (
          <div style={grid}>
            {reqImgs.map((r) => (
              <button key={r.path} type="button" disabled={busy} className="ac-card" style={{ padding: 6, cursor: 'pointer', font: 'inherit' }}
                onClick={() => pick(() => onPickRequest(r.path))}>
                <img src={r.url} alt="" style={{ width: '100%', height: 90, objectFit: 'contain', background: '#fff' }} />
              </button>
            ))}
          </div>
        ))}
      <p style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 12 }}>
        {t('الصورة تُنسخ إلى هذا المستند؛ حذفها لاحقاً لا يؤثر على المصدر.', 'The image is copied into this document; removing it later does not affect the source.')}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="ac-btn" onClick={onClose}>{t('إغلاق', 'Close')}</button></div>
    </Modal>
  );
}

/** Crop to one of a few aspect ratios. onApply receives the area in image pixels. */
export function CropModal({ url, naturalAspect, onApply, onClose, t }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(naturalAspect || 4 / 3);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onComplete = useCallback((_, px) => setArea(px), []);
  const options = [[naturalAspect, t('الأصلي', 'Original')], [1, '1:1'], [4 / 3, '4:3'], [3 / 4, '3:4'], [16 / 9, '16:9']];
  return (
    <Modal title={t('قص الصورة', 'Crop image')} onClose={onClose}>
      <div style={{ position: 'relative', width: '100%', height: 380, background: '#222', borderRadius: 8, overflow: 'hidden' }}>
        <Cropper image={url} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        {options.map(([a, l]) => (
          <button key={l} type="button" className={`ac-chip${Math.abs(aspect - a) < 1e-6 ? ' on' : ''}`} onClick={() => setAspect(a)}>{l}</button>
        ))}
        <input type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ flex: 1, minWidth: 120 }} />
      </div>
      {err && <div className="ac-placeholder" style={{ color: 'var(--ac-danger)' }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="ac-btn" onClick={onClose}>{t('إلغاء', 'Cancel')}</button>
        <button className="ac-btn ac-btn-primary" disabled={!area || busy}
          onClick={async () => { setBusy(true); try { await onApply(area); onClose(); } catch (e) { setErr(e.message); setBusy(false); } }}>
          {busy ? '…' : t('تطبيق', 'Apply')}
        </button>
      </div>
    </Modal>
  );
}
